/**
 * NxConditionGroup ↔ ERP Filter JSON. Flat AND stays in `conditions.ts`;
 * this one keeps nested `and` / `or`, up to six groups deep.
 */
import type { NxConditionGroup, NxConditionNode, NxConditionRule } from '@blpde/nx-ui';
import { draftToFilter, fieldToDraft, newConditionId } from './conditions';
import type { FilterFieldKind } from './operators';

/** A Filter scalar object: `{ field: { op: value } }` or `{ and|or: [...] }`. */
export type Filter = Record<string, unknown>;

/** Root counts as 1. A group past this depth is dropped, not converted. */
const MAX_DEPTH = 6;

function isGroup(node: NxConditionNode): node is NxConditionGroup {
  return 'children' in node && Array.isArray(node.children);
}

function ruleText(rule: NxConditionRule): string | undefined {
  const { value } = rule;
  if (value == null) return undefined;
  if (rule.op === 'in' && Array.isArray(value)) {
    return value.map((item) => (item == null ? '' : String(item))).join(', ');
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
}

/**
 * The group as a Filter object. An empty group, a group whose rules are all
 * incomplete, and a group deeper than {@link MAX_DEPTH} return undefined.
 * A single `and` child is unwrapped, matching the flat converter.
 */
export function conditionGroupToFilter(
  group: NxConditionGroup,
  kindFor?: (field: string) => FilterFieldKind | undefined,
  depth = 1,
): Filter | undefined {
  if (depth > MAX_DEPTH) return undefined;
  const parts: Filter[] = [];
  for (const child of group.children) {
    if (isGroup(child)) {
      const nested = conditionGroupToFilter(child, kindFor, depth + 1);
      if (nested) parts.push(nested);
      continue;
    }
    const value = ruleText(child);
    if (value === undefined) continue;
    const part = draftToFilter({ field: child.field, op: child.op, value }, kindFor);
    if (part) parts.push(part);
  }
  if (parts.length === 0) return undefined;
  if (parts.length === 1 && group.combinator === 'and') return parts[0];
  return { [group.combinator]: parts };
}

/** Filter JSON back into a group. A value that is not a filter becomes an empty `and`. */
export function filterToConditionGroup(filter: unknown): NxConditionGroup {
  const node = filterToNode(filter);
  if (node && isGroup(node)) return node;
  return { id: newConditionId(), combinator: 'and', children: node ? [node] : [] };
}

function filterToNode(filter: unknown): NxConditionNode | undefined {
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return undefined;
  const obj = filter as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (
    keys.length === 1 &&
    (keys[0] === 'and' || keys[0] === 'or') &&
    Array.isArray(obj[keys[0]!])
  ) {
    const combinator = keys[0] as 'and' | 'or';
    const children = (obj[combinator] as unknown[])
      .map((child) => filterToNode(child))
      .filter((child): child is NxConditionNode => child !== undefined);
    return { id: newConditionId(), combinator, children };
  }
  const children: NxConditionNode[] = [];
  for (const [field, raw] of Object.entries(obj)) {
    if ((field === 'and' || field === 'or') && Array.isArray(raw)) {
      const nested = filterToNode({ [field]: raw });
      if (nested) children.push(nested);
      continue;
    }
    const draft = fieldToDraft(field, raw);
    if (!draft) continue;
    const rule: NxConditionRule = {
      id: draft.id,
      field: draft.field,
      op: draft.op,
      value: draft.value,
    };
    children.push(rule);
  }
  if (children.length === 1) return children[0];
  if (children.length === 0) return undefined;
  return { id: newConditionId(), combinator: 'and', children };
}
